import React, { useEffect, useState } from "react";

import { getData } from "../services/FetchNodeservices";
import { FaQuestionCircle, FaFileExport } from "react-icons/fa";
import * as XLSX from "xlsx";
import Header from "../components/Header";
import Footer from "../components/Footer";

export default function PaymentReport() {
  const [companies, setCompanies] = useState([]);
  const [invoices, setInvoices] = useState([]);

  // Pagination states
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [currentPage, setCurrentPage] = useState(1);

  const [formData, setFormData] = useState({
    company_name: "All",
    from_date: "",
    to_date: "",
  });

  const formatDate = (isoDateString) => {
    if (!isoDateString) return "";
    try {
      const date = new Date(isoDateString);
      if (isNaN(date.getTime())) return "";
      const day = String(date.getDate()).padStart(2, "0");
      const month = String(date.getMonth() + 1).padStart(2, "0");
      const year = date.getFullYear();
      return `${day}-${month}-${year}`;
    } catch {
      return "";
    }
  };

  const formatDisplayDate = (dateString) => {
    if (!dateString) return "";
    try {
      const [year, month, day] = dateString.split("-");
      return `${day}-${month}-${year}`;
    } catch {
      return "";
    }
  };

  const fetchCompanies = async () => {
    const response = await getData("api/company");
    if (response.success) setCompanies(response.data);
  };

  useEffect(() => {
    fetchCompanies();
  }, []);

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const showReport = async () => {
    if (!formData.from_date || !formData.to_date) {
      alert("Please select both dates.");
      return;
    }

    const { company_name, from_date, to_date } = formData;

    const result = await getData(
      `api/invoice_master/report?company_name=${encodeURIComponent(
        company_name
      )}&from_date=${from_date}&to_date=${to_date}`
    );

    if (result.success) {
      setInvoices(result.data);
      setCurrentPage(1); // reset pagination
    } else {
      setInvoices([]);
    }
  };

  // ⭐ FIX: Proper pagination values used in table below
  const startIndex =
    rowsPerPage === "All" ? 0 : (currentPage - 1) * rowsPerPage;
  const endIndex =
    rowsPerPage === "All" ? invoices.length : startIndex + rowsPerPage;
  const pagedInvoices = invoices.slice(startIndex, endIndex);

  const totalPages =
    rowsPerPage === "All" ? 1 : Math.ceil(invoices.length / rowsPerPage);

  const changePage = (page) => {
    if (page >= 1 && page <= totalPages) setCurrentPage(page);
  };

  // Excel Export
const exportToExcel = () => {
  if (invoices.length === 0) {
    alert("No data to export.");
    return;
  }

  const dataForExcel = invoices.map((invoice, index) => ({
    "S.No": index + 1,
    Name: invoice.name || "",
    GSTIN: invoice.gstin || "",
    Address: invoice.company_address || "",
    "Invoice Date": formatDate(invoice.invoice_date),
    "Invoice Amount": invoice.total_amount || 0,
    Payment: invoice.amount_paid || 0,
    Outstanding:
      invoice.amount_due ??
      invoice.total_amount - invoice.amount_paid ??
      0,
  }));

  const ws = XLSX.utils.json_to_sheet(dataForExcel);

  // Add only table headers
  const tableHeaders = [
    "S.No",
    "Name",
    "GSTIN",
    "Address",
    "Invoice Date",
    "Invoice Amount",
    "Payment",
    "Outstanding",
  ];

  XLSX.utils.sheet_add_aoa(ws, [tableHeaders], { origin: "A1" });
  XLSX.utils.sheet_add_aoa(ws, dataForExcel.map(Object.values), {
    origin: "A2",
  });

  const workbook = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(workbook, ws, "PaymentReport");

  XLSX.writeFile(workbook, "Payment_Report.xlsx");
};


  const reportTitle =
    invoices.length > 0
      ? `Morgan Placement & Consultancy Pvt. Ltd. Payment List From Date: ${formatDisplayDate(
          formData.from_date
        )} To Date: ${formatDisplayDate(formData.to_date)}`
      : "Payment Report";

  return (
    <>
    {/* <Header/> */}

      <div className="
    p-6 bg-[#EDF1F7] min-h-screen
    mt-16
    ml-0 md:ml-72
">

        <div className="bg-white shadow-md rounded-md p-6 border">
          {/* HEADER */}
          <div className="flex justify-between items-center mb-4">
            <h2 className="text-xl font-semibold">{reportTitle}</h2>

            <div className="flex items-center gap-4">
              <button
                onClick={exportToExcel}
                className="flex items-center gap-2 bg-green-600 text-white px-3 py-2 rounded hover:bg-green-700"
              >
                <FaFileExport /> Export Excel
              </button>
              <FaQuestionCircle className="text-gray-500 cursor-pointer" />
            </div>
          </div>

          {/* FILTERS */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <div>
              <label className="block text-gray-600 font-medium mb-1">
                Select Company
              </label>
              <select
                name="company_name"
                value={formData.company_name}
                onChange={handleChange}
                className="border p-2 rounded w-full"
              >
                <option value="All">All</option>
                {companies.map((c) => (
                  <option key={c.id} value={c.name}>
                    {c.name}
                  </option>
                ))}
              </select>
            </div>

            <div>
              <label className="block text-gray-600 font-medium mb-1">
                From*
              </label>
              <input
                type="date"
                name="from_date"
                value={formData.from_date}
                onChange={handleChange}
                className="border p-2 rounded w-full"
              />
            </div>

            <div>
              <label className="block text-gray-600 font-medium mb-1">
                To*
              </label>
              <input
                type="date"
                name="to_date"
                value={formData.to_date}
                onChange={handleChange}
                className="border p-2 rounded w-full"
              />
            </div>
          </div>

          {/* Show Report */}
          <div className="mt-6">
            <button
              onClick={showReport}
              className="bg-red-600 text-white px-4 py-2 rounded hover:bg-red-700"
            >
              Show Report
            </button>
          </div>
        </div>

        {/* TABLE */}
        {invoices.length > 0 && (
          <div className="mt-6 bg-white p-4 shadow rounded border">
            {/* ROWS PER PAGE */}
            <div className="flex justify-end mb-3">
              <select
                value={rowsPerPage}
                onChange={(e) => {
                  setRowsPerPage(
                    e.target.value === "All"
                      ? "All"
                      : parseInt(e.target.value)
                  );
                  setCurrentPage(1);
                }}
                className="border p-2 rounded"
              >
                <option value="10">10</option>
                <option value="20">20</option>
                <option value="50">50</option>
                <option value="All">All</option>
              </select>
            </div>

            {/* TABLE */}
            <div className="overflow-auto">
              <table className="w-full border border-gray-300">
                <thead>
                  <tr className="bg-gray-200 text-left whitespace-nowrap">
                    <th className="border p-2">S.No</th>
                    <th className="border p-2">Name</th>
                    <th className="border p-2">GSTIN</th>
                    <th className="border p-2">Address</th>
                    <th className="border p-2">Invoice Date</th>
                    <th className="border p-2">Invoice Amount</th>
                    <th className="border p-2">Payment</th>
                    <th className="border p-2">Outstanding</th>
                  </tr>
                </thead>

                <tbody>
                  {pagedInvoices.map((row, i) => (
                    <tr key={i} className="whitespace-nowrap">
                      <td className="border p-2">{startIndex + i + 1}</td>

                      <td className="border p-2">{row.name || "-"}</td>
                      <td className="border p-2">{row.gstin || "-"}</td>
                      <td className="border p-2">
                        {row.company_address || "-"}
                      </td>
                      <td className="border p-2">
                        {formatDate(row.invoice_date)}
                      </td>

                      <td className="border p-2">
                        {Number(row.total_amount || 0).toFixed(2)}
                      </td>

                      <td className="border p-2">
                        {Number(row.amount_paid || 0).toFixed(2)}
                      </td>

                      <td className="border p-2">
                        {Number(
                          row.amount_due ??
                            (Number(row.total_amount || 0) -
                              Number(row.amount_paid || 0))
                        ).toFixed(2)}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* PAGINATION */}
            {rowsPerPage !== "All" && (
              <div className="flex justify-center mt-4 gap-2">
                <button
                  onClick={() => changePage(currentPage - 1)}
                  className="px-3 py-1 border rounded bg-gray-100"
                >
                  Prev
                </button>

                {[...Array(totalPages).keys()].map((n) => (
                  <button
                    key={n}
                    onClick={() => changePage(n + 1)}
                    className={`px-3 py-1 border rounded ${
                      currentPage === n + 1
                        ? "bg-blue-500 text-white"
                        : "bg-gray-100"
                    }`}
                  >
                    {n + 1}
                  </button>
                ))}

                <button
                  onClick={() => changePage(currentPage + 1)}
                  className="px-3 py-1 border rounded bg-gray-100"
                >
                  Next
                </button>
              </div>
            )}
          </div>
        )}
      </div>
      {/* <Footer/> */}
    </>
  );
}
